import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Flex, Box, Text, useMediaQuery, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {noop, remToPxConverter} from '../../utils/utils'
import {breakpoints, remCalc} from '../../theme/_utils'
import Field from '../field'
import {MESSAGE_PROPTYPE} from '../../utils/locale'
import useAddressFields from '../forms/use-address-fields'
import {AdviseIcon} from '../icons/others'
import {useCountryLabels} from './use-country-labels'
import {
    useBooleanCustomPreference,
    useGlobalCustomPreference,
} from '../../hooks/use-custom-preference'
import {useLocaleCountry} from '../../hooks/use-locale-custom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isPecualiarAddressFormCountry} from '../../utils/peculiar-address-form'

const LoqateFields = ({
    form,
    defaultShowAutocomplete = true,
    prefix = '',
    isBillingAddress = false,
    addressType = 'private',
    variant = 'default',
    action = noop,
    isContactUs = false,
    contactUsConfigFields = {},
    setAutocompleteObj,
}) => {
    const styles = useMultiStyleConfig('AddressFields', {variant})
    const [isMobile] = useMediaQuery(`(max-width: ${remToPxConverter(breakpoints.md) - 1}px)`)

    const [showAutocomplete, setShowAutocomplete] = useState(defaultShowAutocomplete)
    const [autocompleteAvailable, setAutocompleteAvailability] = useState(true)

    const setAutocompleteAvailable = (stato) => {
        setAutocompleteAvailability(stato)
    }
    const regions = useGlobalCustomPreference('regionConfig')
    const values = form.getValues()
    const localeCountry = useLocaleCountry()

    const {getCountries, getEuropeanCountries} = useCountryLabels()

    const [showAddress2, setShowAddress2] = useState(false)
    const isLoqateFlag = useBooleanCustomPreference('showLoqateFlag')
    const isCurrentCountryEuropean = getEuropeanCountries().some((c) => c.value === localeCountry)
    //geo blocking only for EU member state and for billingaddress
    const fields = useAddressFields({
        form,
        prefix,
        countries:
            isBillingAddress && isCurrentCountryEuropean ? getEuropeanCountries() : getCountries(),
        isBillingAddress: isBillingAddress,
        country: values?.countryCode?.value || values?.countryCode,
        isCurrentCountryEuropean,
    })

    useEffect(() => {
        if (values.address1) {
            setShowAutocomplete(false)
        }
    }, [])

    const watcherStateCode = form.watch('stateCode')
    const watcheraddress2 = form.watch('address2')
    const selectedCountryWatcher = form.watch(`countryCode`)
    const {locale} = useMultiSite()
    const isPeculiarAddressFormCountry = isPecualiarAddressFormCountry(locale)
    const selectedCountry =
        typeof selectedCountryWatcher === 'string'
            ? selectedCountryWatcher
            : selectedCountryWatcher?.value
    const province = useMemo(() => {
        const stateCode =
            typeof watcherStateCode === 'string' ? watcherStateCode : watcherStateCode?.value

        return fields.stateCode.options?.find((province) => province.value === stateCode)
    }, [])

    const addressFormRef = useRef()

    useEffect(() => {
        if (province) form.setValue('stateCode', province)
        // Focus on the form when the component mounts for accessibility
        addressFormRef?.current?.focus()
    }, [])

    // reset stateCode when country changes, only for billing address forms
    const countryCodeRef = useRef(selectedCountry)
    useEffect(() => {
        if (isBillingAddress && countryCodeRef.current !== selectedCountry) {
            form.setValue('stateCode', '')
            countryCodeRef.current = selectedCountry
        }
    }, [selectedCountry])

    const [lookupObject, setLookupObject] = useState(null) // Store the lookup object

    const updateAddressLookup = (obj = undefined) => {
        setLookupObject(obj) // Store the lookup object for conditional checks
        form.setValue(fields.addressLookup.name, obj)
    }

    const onAddressUpdate = (lookupObject) => {
        if (lookupObject) {
            setAutocompleteObj && setAutocompleteObj(lookupObject)
            if (isBillingAddress && isCurrentCountryEuropean) {
                form.setValue(fields.countryCode.name, {
                    value: lookupObject.CountryIso2,
                    label: lookupObject.CountryName,
                })
            }
            form.setValue(fields.city.name, lookupObject.City)

            lookupObject.PostalCode = isContactUs
                ? lookupObject.PostalCode.replaceAll(' ', '')
                : lookupObject.PostalCode

            form.setValue(fields.postalCode.name, lookupObject.PostalCode)

            const isAddressNumberSeparate = isContactUs && contactUsConfigFields.addressNumber

            // Set address1
            const address1Value = isAddressNumberSeparate
                ? lookupObject.Street // No address number included
                : lookupObject.Line1 // Address number included

            form.setValue(fields.address1.name, address1Value)

            // Set addressNumber if it's a separate field
            if (isAddressNumberSeparate) {
                form.setValue(fields.addressNumber.name, lookupObject.BuildingNumber)
            }

            setTimeout(() => {
                const provinceLabel = fields.stateCode.options?.find(
                    (province) => province.value === lookupObject.ProvinceCode,
                )?.label
                if (
                    regions?.[lookupObject.CountryIso2] &&
                    regions?.[lookupObject.CountryIso2]?.length > 0 &&
                    lookupObject.ProvinceCode &&
                    provinceLabel
                ) {
                    // Keep - The select of regions takes a while to update after changing the state code
                    form.setValue(fields.stateCode.name, {
                        value: lookupObject.ProvinceCode,
                        label: provinceLabel,
                    })
                } else if (lookupObject.ProvinceName && provinceLabel) {
                    form.setValue(fields.stateCode.name, provinceLabel)
                } else {
                    form.setValue(fields.stateCode.name, '')
                }
            }, 500)

            setShowAutocomplete(!showAutocomplete)
        }
        updateAddressLookup(lookupObject)
    }

    const refreshForm = () => {
        form.setValue(fields.addressLookup.name, null)
        form.clearErrors(fields.address1.name)
        setShowAutocomplete(!showAutocomplete)
    }
    return isPeculiarAddressFormCountry ? (
        <>
            {/* Unavailable Lookup Alert */}
            {!isLoqateFlag && showAutocomplete && !autocompleteAvailable && (
                <Text {...styles.unavailable}>
                    <FormattedMessage
                        defaultMessage="Address lookup service is unavailable. Please enter your address manually."
                        id="address_fields.error.lookup_service_unavailable"
                    />
                </Text>
            )}

            {(!showAutocomplete || !autocompleteAvailable || !isLoqateFlag) && (
                <>
                    {isBillingAddress && addressType === 'business' && (
                        <Flex
                            {...{
                                textStyle: 'body-xsmall',
                                gap: remCalc(8),
                                paddingTop: remCalc(16),
                                borderTop: 'small',
                                borderStyle: 'solid',
                                borderColor: 'border.light',
                                cursor: 'pointer',
                                alignItems: 'center',
                            }}
                            onClick={() => action()}
                        >
                            <AdviseIcon />
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Click to fill in the fields below with your shipping address data."
                                    id="address_fields.label.click_to_fill_address"
                                />
                            </Text>
                        </Flex>
                    )}

                    <Flex {...styles.gaps}>
                        <Box
                            flex={
                                isContactUs &&
                                (!contactUsConfigFields.addressNumber || !lookupObject)
                                    ? '4' // Full width when addressNumber is missing
                                    : '3' // Default width when addressNumber is present
                            }
                        >
                            <Field {...fields.address1} />
                        </Box>
                        {isContactUs &&
                            ((contactUsConfigFields.addressNumber && !lookupObject) ||
                                lookupObject) && (
                                <Box flex="1">
                                    <Field {...fields.addressNumber} />
                                </Box>
                            )}
                    </Flex>

                    {showAddress2 || watcheraddress2 ? (
                        <Field {...fields.address2} />
                    ) : (
                        <Button
                            {...styles.backToLookupBtn}
                            data-testid="show-address2-btn"
                            onClick={() => setShowAddress2(true)}
                        >
                            <FormattedMessage
                                defaultMessage="Add more details"
                                id="address_fields.label.add_more_details"
                            />
                        </Button>
                    )}

                    <Flex {...styles.gaps}>
                        {(!isContactUs ||
                            (contactUsConfigFields.countryCode && !lookupObject) ||
                            lookupObject) && <Field {...fields.countryCode} isSearchable={true} />}
                        {(!isContactUs ||
                            (contactUsConfigFields.postalCode && !lookupObject) ||
                            lookupObject) && <Field {...fields.postalCode} />}
                    </Flex>

                    <Flex {...styles.gaps}>
                        {
                            // Render each address field if:
                            // - Not on Contact Us page
                            // - OR the field is configured to be shown and not using address lookup (not autocomplete)
                            // - OR address was found via lookup (autocomplete)
                        }
                        {(!isContactUs ||
                            (contactUsConfigFields.stateCode && !lookupObject) ||
                            lookupObject) && <Field {...fields.stateCode} isSearchable={true} />}
                        {(!isContactUs ||
                            (contactUsConfigFields.city && !lookupObject) ||
                            lookupObject) && <Field {...fields.city} />}
                    </Flex>

                    <Flex>
                        {isLoqateFlag && autocompleteAvailable && (
                            <Button
                                {...styles.backToLookupBtn}
                                data-testid="lookup-btn"
                                onClick={() => refreshForm('manual')}
                            >
                                <FormattedMessage
                                    defaultMessage="Back to address lookup"
                                    id="address_fields.label.backToAddressLookup"
                                />
                            </Button>
                        )}
                    </Flex>
                </>
            )}
            {isLoqateFlag && showAutocomplete && autocompleteAvailable && (
                <Flex zIndex={2} {...styles.gaps}>
                    <Box flex="3">
                        {/* Address lookup field for autocomplete */}
                        <Field
                            {...fields.addressLookup}
                            setAutocompleteAvailable={setAutocompleteAvailable}
                            toggleAutocomplete={() => refreshForm('lookup')}
                            onAddressUpdate={onAddressUpdate}
                            elementsCount={isMobile ? 3 : 5}
                        />
                    </Box>
                    {isContactUs && (
                        <Box flex="1">
                            {/* Address number field is always visible for autocomplete but only for Contact Us */}
                            <Field {...fields.addressNumber} />
                        </Box>
                    )}
                </Flex>
            )}
        </>
    ) : (
        <>
            {/* Unavailable Lookup Alert */}
            {!isLoqateFlag && showAutocomplete && !autocompleteAvailable && (
                <Text {...styles.unavailable}>
                    <FormattedMessage
                        defaultMessage="Address lookup service is unavailable. Please enter your address manually."
                        id="address_fields.error.lookup_service_unavailable"
                    />
                </Text>
            )}

            {(!showAutocomplete || !autocompleteAvailable || !isLoqateFlag) && (
                <>
                    {isBillingAddress && addressType === 'business' && (
                        <Flex
                            {...{
                                textStyle: 'body-xsmall',
                                gap: remCalc(8),
                                paddingTop: remCalc(16),
                                borderTop: 'small',
                                borderStyle: 'solid',
                                borderColor: 'border.light',
                                cursor: 'pointer',
                                alignItems: 'center',
                            }}
                            onClick={() => action()}
                        >
                            <AdviseIcon />
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Click to fill in the fields below with your shipping address data."
                                    id="address_fields.label.click_to_fill_address"
                                />
                            </Text>
                        </Flex>
                    )}

                    <Flex {...styles.gaps}>
                        <Box flex="3">
                            <Field {...fields.address1} />
                        </Box>
                        {isContactUs &&
                            ((contactUsConfigFields.addressNumber && !lookupObject) ||
                                (lookupObject && contactUsConfigFields.addressNumber)) && (
                                <Box flex="1">
                                    <Field {...fields.addressNumber} />
                                </Box>
                            )}
                    </Flex>

                    {!isContactUs &&
                        (showAddress2 || watcheraddress2 ? (
                            <Field {...fields.address2} />
                        ) : (
                            <Button
                                {...styles.backToLookupBtn}
                                data-testid="show-address2-btn"
                                onClick={() => setShowAddress2(true)}
                            >
                                <FormattedMessage
                                    defaultMessage="Add more details"
                                    id="address_fields.label.add_more_details"
                                />
                            </Button>
                        ))}
                    {(!isContactUs ||
                        contactUsConfigFields.city ||
                        contactUsConfigFields.stateCode ||
                        (lookupObject && !isContactUs)) && (
                        <Flex {...styles.gaps}>
                            {(!isContactUs || contactUsConfigFields.city || lookupObject) && (
                                <Field {...fields.city} />
                            )}
                            {(!isContactUs || contactUsConfigFields.stateCode || lookupObject) && (
                                <Field {...fields.stateCode} isSearchable={true} />
                            )}
                        </Flex>
                    )}
                    {(!isContactUs ||
                        contactUsConfigFields.postalCode ||
                        contactUsConfigFields.countryCode ||
                        (lookupObject && !isContactUs)) && (
                        <Flex {...styles.gaps}>
                            {(!isContactUs || contactUsConfigFields.postalCode || lookupObject) && (
                                <Field {...fields.postalCode} />
                            )}
                            {(!isContactUs ||
                                contactUsConfigFields.countryCode ||
                                lookupObject) && (
                                <Field {...fields.countryCode} isSearchable={true} />
                            )}
                        </Flex>
                    )}

                    {!isContactUs && (
                        <Flex>
                            {isLoqateFlag && autocompleteAvailable && (
                                <Button
                                    {...styles.backToLookupBtn}
                                    data-testid="lookup-btn"
                                    onClick={() => refreshForm('manual')}
                                >
                                    <FormattedMessage
                                        defaultMessage="Back to address lookup"
                                        id="address_fields.label.backToAddressLookup"
                                    />
                                </Button>
                            )}
                        </Flex>
                    )}
                </>
            )}
            {isLoqateFlag && showAutocomplete && autocompleteAvailable && !isContactUs && (
                <Flex zIndex={2} {...styles.gaps}>
                    <Box flex="3">
                        {/* Address lookup field for autocomplete */}
                        <Field
                            {...fields.addressLookup}
                            setAutocompleteAvailable={setAutocompleteAvailable}
                            toggleAutocomplete={() => refreshForm('lookup')}
                            onAddressUpdate={onAddressUpdate}
                            elementsCount={isMobile ? 3 : 5}
                            isContactUs={isContactUs}
                        />
                    </Box>
                    {isContactUs && contactUsConfigFields.addressNumber && (
                        <Box flex="1">
                            {/* Address number field visible for autocomplete only if it's in the preference */}
                            <Field {...fields.addressNumber} />
                        </Box>
                    )}
                </Flex>
            )}
            {isLoqateFlag && showAutocomplete && autocompleteAvailable && isContactUs && (
                <>
                    <Flex zIndex={2} {...styles.gaps}>
                        <Box flex="3">
                            {/* Address lookup field for autocomplete */}
                            <Field
                                {...fields.addressLookup}
                                setAutocompleteAvailable={setAutocompleteAvailable}
                                toggleAutocomplete={() => refreshForm('lookup')}
                                onAddressUpdate={onAddressUpdate}
                                elementsCount={isMobile ? 3 : 5}
                                isContactUs={isContactUs}
                            />
                        </Box>
                        {isContactUs && contactUsConfigFields.addressNumber && (
                            <Box flex="1">
                                {/* Address number field visible for autocomplete only if it's in the preference */}
                                <Field {...fields.addressNumber} />
                            </Box>
                        )}
                    </Flex>
                    {(contactUsConfigFields.city || contactUsConfigFields.stateCode) && (
                        <Flex {...styles.gaps}>
                            {contactUsConfigFields.city && <Field {...fields.city} />}
                            {contactUsConfigFields.stateCode && (
                                <Field {...fields.stateCode} isSearchable={true} />
                            )}
                        </Flex>
                    )}
                    {(contactUsConfigFields.postalCode || contactUsConfigFields.countryCode) && (
                        <Flex {...styles.gaps}>
                            {contactUsConfigFields.postalCode && <Field {...fields.postalCode} />}
                            {contactUsConfigFields.countryCode && (
                                <Field {...fields.countryCode} isSearchable={true} />
                            )}
                        </Flex>
                    )}
                </>
            )}
        </>
    )
}

LoqateFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,
    /** Optional prefix for field names */
    prefix: PropTypes.string,
    edit: PropTypes.object,
    fromEdit: PropTypes.bool,
    /** Default autocomplete visibility status */
    defaultShowAutocomplete: PropTypes.bool,
    enableEditing: PropTypes.bool,

    /** Optional aria label to use for the address form */
    formTitleAriaLabel: MESSAGE_PROPTYPE,

    /** Optional flag to indication if an address is a billing address */
    isBillingAddress: PropTypes.bool,
    addressType: PropTypes.oneOf(['private', 'business']),
    action: PropTypes.func,
    variant: PropTypes.string,
    isContactUs: PropTypes.bool,
    contactUsConfigFields: PropTypes.object,
    setAutocompleteObj: PropTypes.func,
}

export default LoqateFields
